
import React, { useState, useContext, useEffect } from 'react'
import {
	Button,
	Card,
	CardBody,
	Col,
	Modal,
	Row,
	Table
} from 'reactstrap'
import { useTranslation } from 'react-i18next'
import { SpinnerComponent } from 'react-element-spinner'

import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
dayjs.extend(localizedFormat)
dayjs.extend(utc)
dayjs.extend(timezone)

import AuctionTabsService from '../../services/AuctionTabsService'
import SiteService from '../../services/SiteService'
import { SessionContext } from '../../contexts/SessionProvider'

const PricingHistoryModal = ({ isOpen = false, auction = null, onClose }) =>
{
	const [isModalBusy, setIsModalBusy] = useState(false)
	const { t } = useTranslation()
	const sessionContext = useContext(SessionContext)
	const [historyEntries, setHistoryEntries] = useState([])

	useEffect(() =>
	{
		if (isOpen && auction?.auctionId)
		{
			retrievePricingHistory()
		}
	}, [isOpen, auction?.auctionId])

	const retrievePricingHistory = () =>
	{
		setIsModalBusy(true)
		AuctionTabsService
			.getPricingHistory(auction.auctionId, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsModalBusy(false)
				if (result.data?.loginSession)
				{
					sessionContext.updateSession(result.data.loginSession)
				}
				// API returns { actionResult: { auctionId, vin, yearMakeModel, entries: [...] } }
				const payload = result.data?.actionResult ?? result.data ?? {}
				const entries = Array.isArray(payload) ? payload : (payload.entries || [])
				setHistoryEntries(Array.isArray(entries) ? entries : [])
			})
			.catch((error) =>
			{
				setIsModalBusy(false)
				SiteService.displayApiError(error)
				setHistoryEntries([])
			})
	}

	const handleCloseModal = () =>
	{
		setHistoryEntries([])
		if (typeof onClose === 'function')
		{
			onClose()
		}
	}

	const formatDate = (dateValue) =>
	{
		if (!dateValue) return '—'
		// AuctionStatusHistories.Created is stored in UTC (DateTimeOffset.UtcNow) but the
		// API projects it as a naive DateTime, so interpret it as UTC and convert to
		// Eastern (EST/EDT) for display rather than rendering the raw UTC clock time.
		return dayjs.utc(dateValue).tz('America/Toronto').format('YYYY-MM-DD, h:mm A') + ' ET'
	}

	const formatCurrency = (value) =>
	{
		if (value === null || value === undefined) return '$0'
		return `$${Number(value).toLocaleString()}`
	}

	/**
	 * Format pricing display per BRD screenshot:
	 * "MBP: $10,500, Start: $10,000, Reserve: $10,500, Buy Now: $11,000"
	 */
	const formatPricing = (entry) => {
		const parts = []
		if (entry.mbpPrice !== undefined && entry.mbpPrice !== null) {
			parts.push(`MBP: ${formatCurrency(entry.mbpPrice)}`)
		}
		if (entry.startPrice !== undefined && entry.startPrice !== null) {
			parts.push(`Start: ${formatCurrency(entry.startPrice)}`)
		}
		if (entry.reservePrice !== undefined && entry.reservePrice !== null) {
			parts.push(`Reserve: ${formatCurrency(entry.reservePrice)}`)
		}
		if (entry.buyNowPrice !== undefined && entry.buyNowPrice !== null) {
			parts.push(`Buy Now: ${formatCurrency(entry.buyNowPrice)}`)
		}
		// BRD: Show the actual price the vehicle was sold for on Sold rows.
		if (entry.soldPrice !== undefined && entry.soldPrice !== null) {
			parts.push(`Sold For: ${formatCurrency(entry.soldPrice)}`)
		}
		// BRD: Also show bid/counter info if present
		if (entry.highBid !== undefined && entry.highBid !== null) {
			parts.push(`High Bid: ${formatCurrency(entry.highBid)}`)
		}
		if (entry.counterAmount !== undefined && entry.counterAmount !== null) {
			parts.push(`Counter: ${formatCurrency(entry.counterAmount)}`)
		}
		// BRD: Show "Sold for counter" type messages
		if (entry.pricingNote) {
			parts.push(entry.pricingNote)
		}
		return parts.length > 0 ? parts.join(', ') : '—'
	}

	/**
	 * Get iteration display per BRD: 1, 2, 3, 4, etc.
	 */
	const getIterationDisplay = (entry) => {
		if (entry.iterationId) return entry.iterationId.toString()
		if (entry.iteration) return entry.iteration.toString()
		if (entry.currentIteration) {
			// Convert "MBP" to 1, "IT2" to 2, etc.
			const iterName = entry.currentIteration.toUpperCase()
			if (iterName === 'MBP') return '1'
			if (iterName === 'IT2') return '2'
			if (iterName === 'IT3') return '3'
			if (iterName === 'IT4') return '4'
			if (iterName.includes('OPENLANE')) return '5'
			return entry.currentIteration
		}
		return '—'
	}

	const vehicleDescription = `${auction?.modelYear || auction?.year || ''} ${auction?.make || ''} ${auction?.model || ''}`.trim() || 'Selected Vehicle'

	return (
		<Modal
			className="modal-dialog-centered"
			size="xl"
			isOpen={isOpen}
		>
			<SpinnerComponent
				loading={isModalBusy}
				position="centered"
				message="Loading history..."
			/>
			<Card className="mb-0">
				<CardBody>
					{/* BRD: "Identify the vehicle that you are reviewing at the top" */}
					<div className="mb-4">
						<h5 className="mb-2">History:</h5>
						<div>
							<strong>Vehicle:</strong> {vehicleDescription}
						</div>
						{auction?.vin && (
							<div>
								<strong>VIN:</strong> {auction.vin}
							</div>
						)}
					</div>
					
					{historyEntries.length > 0 ? (
						<div className="table-responsive" style={{ maxHeight: '450px', overflowY: 'auto' }}>
							{/* BRD Table: Date/Time, Iteration, Status, Pricing, User */}
							<Table bordered size="sm">
								<thead className="bg-light" style={{ position: 'sticky', top: 0 }}>
									<tr>
										<th style={{ minWidth: '140px' }}>Date/Time</th>
										<th style={{ width: '70px' }}>Iteration</th>
										<th style={{ minWidth: '120px' }}>Status</th>
										<th>Pricing</th>
										<th style={{ width: '100px' }}>User</th>
									</tr>
								</thead>
								<tbody>
									{historyEntries.map((entry, index) => (
									<tr key={entry.historyId || entry.id || index}>
										<td className="text-nowrap" style={{ fontSize: '0.85rem' }}>
											{formatDate(entry.dateTime || entry.createdDate || entry.changedDate || entry.date)}
										</td>
										<td style={{ fontSize: '0.85rem', textAlign: 'center' }}>
											{getIterationDisplay(entry)}
										</td>
										<td style={{ fontSize: '0.85rem' }}>
											{entry.actionName || entry.newStatus || entry.status || entry.eventType || entry.changeType || entry.auctionState || '—'}
											</td>
											<td style={{ fontSize: '0.85rem' }}>
												{formatPricing(entry)}
											</td>
											<td style={{ fontSize: '0.85rem' }}>
												{entry.userName || entry.changedBy || entry.createdBy || entry.user || 'System'}
											</td>
										</tr>
									))}
								</tbody>
							</Table>
						</div>
					) : !isModalBusy && (
						<div className="text-center text-muted py-4">
							<p>No history available for this auction.</p>
						</div>
					)}
					
					<Row className="mt-4">
						<Col className="text-end">
							<Button
								color="secondary"
								onClick={handleCloseModal}
							>
								Close
							</Button>
						</Col>
					</Row>
				</CardBody>
			</Card>
		</Modal>
	)
}

export default PricingHistoryModal
